import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaNewspaper, FaCalendarAlt, FaEye } from 'react-icons/fa';

const News = () => {
  const [newsData, setNewsNews] = useState([]);
  const { t } = useTranslation();
  const newsRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (newsRef.current) observer.observe(newsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const cachedData = localStorage.getItem('newsData');
        const cachedTimestamp = localStorage.getItem('newsDataTimestamp');
  
        const isCacheValid = cachedTimestamp && (new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime()) < 3 * 3600 * 1000;
  
        if (cachedData && isCacheValid) {
          const parsedData = JSON.parse(cachedData);
          setNewsNews(parsedData);
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/news');
          const data = await response.json();
          const sortedNews = data.data;
          setNewsNews(sortedNews);
          localStorage.setItem('newsData', JSON.stringify(sortedNews));
          localStorage.setItem('newsDataTimestamp', new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching news data:', error);
      }
    };
  
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('ru', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return '';
    }
  };

  return (
    <section className="news-modern page-news" ref={newsRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaNewspaper className="me-2" />
            <span>{t('news.badge')}</span>
          </div>
          <h2 className="section-title text-glow text-white">
            {t('news.title')}
          </h2>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">
            {t('news.subtitle')}
          </p>
        </div>

        <div className='row mt-5 fade-in-up stagger-2'>
          {Array.isArray(newsData) ? (
            <>
              {newsData.map((newsItem, index) => (
                <div key={index} className="col-md-4 mb-4 col-12 fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Link to={`/news/${newsItem.slug}`} style={{ textDecoration: 'none' }}>
                    <div className="card glass-container news-card-modern">
                      <div className="news-image-container">
                        <img 
                          className="img-fluid news-image" 
                          src={newsItem.image || '/assets/images/news-default.jpg'} 
                          alt={newsItem.title || 'News Image'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                          }}
                        />
                        <div className="news-overlay">
                          <div className="news-meta">
                            <span className="news-date">
                              <FaCalendarAlt className="me-1" />
                              {formatDate(newsItem.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <h5 className="text-white news-title">{newsItem.title}</h5>
                        <div className="news-divider"></div>
                        
                        <p className='text-white-50 news-description'>
                          {newsItem.description}
                        </p>
                        
                        <div className="mt-4">
                          <button className="btn btn-hero-primary btn-sm w-100 pulse-glow">
                            <FaEye className="me-2" />
                            {t('news.readMore')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </>
          ) : (
            <div className="text-center">
              <h3 className='text-white'>{t('news.error')}</h3>
            </div>
          )}
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="news-bg-decoration"></div>
    </section>
  );
};

export default News;